import { CONFIG } from '../config.js';
import { getPageIds, hasNextPage } from '../utils/pagination.js';
import { toggleFavoriteId } from '../store/selectors.js';
import { readJSON, writeJSON } from '../utils/storage.js';

/**
 * ─── DESIGN PATTERN: FACADE ───────────────────────────────────────────────
 *
 * Il service è l'unico punto di contatto tra la UI e il resto del sistema:
 * traduce le intenzioni dell'utente ("carica altre news", "aggiorna",
 * "salva nei preferiti") in una sequenza di chiamate al Repository e di
 * aggiornamenti dello Store.
 *
 * Riceve le sue dipendenze dall'esterno, quindi nei test si può usare uno
 * Store vero e un Repository finto: si verifica il comportamento completo
 * senza toccare la rete.
 */

/**
 * @param {object} deps
 * @param {import('../api/hackerNewsRepository.js').HackerNewsRepository} deps.repository
 * @param {ReturnType<import('../store/store.js').createStore>} deps.store
 * @param {number} [deps.pageSize]
 */
export function createNewsService({ repository, store, pageSize = CONFIG.PAGE_SIZE }) {
  if (!repository) throw new TypeError('createNewsService richiede un repository');
  if (!store) throw new TypeError('createNewsService richiede uno store');

  /**
   * Traduce un errore tecnico in un messaggio comprensibile.
   * @param {unknown} error
   * @returns {string}
   */
  function toUserMessage(error) {
    if (error?.code === 'ECONNABORTED') {
      return 'La richiesta ha impiegato troppo tempo. Controlla la connessione e riprova.';
    }
    const status = error?.response?.status;
    if (status) return `Il servizio Hacker News ha risposto con un errore (${status}).`;
    return 'Non riesco a contattare Hacker News. Verifica la connessione e riprova.';
  }

  /**
   * Scarica il dettaglio della pagina indicata e la aggiunge allo stato.
   *
   * @param {number} page indice 0-based
   * @returns {Promise<object[]>} le news aggiunte
   */
  async function fetchPage(page) {
    const { storyIds } = store.getState();
    const pageIds = getPageIds(storyIds, page, pageSize);
    if (pageIds.length === 0) return [];

    const { stories, failed } = await repository.getStoriesByIds(pageIds);

    store.setState((state) => ({
      stories: [...state.stories, ...stories],
      loadedPages: page + 1,
    }));

    if (failed > 0) {
      console.warn(`[newsService] ${failed} news non caricate nella pagina ${page + 1}`);
    }

    return stories;
  }

  /**
   * Avvio dell'applicazione: elenco degli ID + dettaglio della prima pagina.
   * @returns {Promise<object[]>}
   */
  async function loadInitial() {
    store.setState({ isLoading: true, error: null });

    try {
      const storyIds = await repository.getNewStoryIds();
      store.setState({ storyIds, stories: [], loadedPages: 0 });
      return await fetchPage(0);
    } catch (error) {
      store.setState({ error: toUserMessage(error) });
      throw error;
    } finally {
      store.setState({ isLoading: false });
    }
  }

  /**
   * "Load more": carica i 10 ID successivi della lista originale.
   * @returns {Promise<object[]>} le news aggiunte (vuoto se non c'è altro)
   */
  async function loadMore() {
    const { storyIds, loadedPages, isLoadingMore, isLoading } = store.getState();

    // Doppio click o click durante il caricamento iniziale: nessuna azione.
    if (isLoadingMore || isLoading) return [];
    if (!hasNextPage(storyIds, loadedPages, pageSize)) return [];

    store.setState({ isLoadingMore: true, error: null });

    try {
      return await fetchPage(loadedPages);
    } catch (error) {
      store.setState({ error: toUserMessage(error) });
      throw error;
    } finally {
      store.setState({ isLoadingMore: false });
    }
  }

  /** Ricarica tutto da zero, svuotando la cache del repository. */
  async function refresh() {
    repository.clearCache?.();
    return loadInitial();
  }

  /** @param {string} value */
  function setSearch(value) {
    store.setState({ search: String(value ?? '') });
  }

  /** @param {string} value */
  function setSort(value) {
    store.setState({ sort: value });
  }

  /** Attiva/disattiva il filtro "solo preferiti". */
  function toggleFavoritesFilter() {
    store.setState((state) => ({ showFavoritesOnly: !state.showFavoritesOnly }));
  }

  /**
   * Aggiunge o rimuove una news dai preferiti e persiste la scelta.
   * @param {number} id
   * @returns {boolean} `true` se dopo l'operazione la news è tra i preferiti
   */
  function toggleFavorite(id) {
    const favorites = toggleFavoriteId(store.getState().favorites, id);
    store.setState({ favorites });
    writeJSON(CONFIG.STORAGE_KEYS.FAVORITES, favorites);
    return favorites.includes(id);
  }

  /** Ripristina i preferiti salvati nella sessione precedente. */
  function restoreFavorites() {
    const saved = readJSON(CONFIG.STORAGE_KEYS.FAVORITES, []);
    const favorites = Array.isArray(saved) ? saved.filter(Number.isInteger) : [];
    store.setState({ favorites });
    return favorites;
  }

  return {
    loadInitial,
    loadMore,
    refresh,
    setSearch,
    setSort,
    toggleFavoritesFilter,
    toggleFavorite,
    restoreFavorites,
  };
}
